import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { fetchGamify } from "@/lib/api";
import { notifAllowed } from "@/lib/notifTiming";
import { Sparkles, X, ArrowRight, Flame } from "lucide-react";
import { onMenu, isMenuOpen } from "@/lib/menuBus";

// A single, gentle, gamified nudge card. Chooses the most relevant next step
// from the user's sadhana state. Non-intrusive: appears after a delay and is
// dismissible for the day. Hidden on the reader, admin and auth screens.
const todayKey = () => `dharmartha_nudge_${new Date().toDateString()}`;

export default function EngagementNudge() {
  const { user } = useAuth();
  const { pick } = useLang();
  const { system_messages } = useSettings();
  const engSm = (system_messages || {}).nudge_engagement || {};
  const { pathname } = useLocation();
  const [nudge, setNudge] = useState(null);
  const [show, setShow] = useState(false);

  const hidden = pathname.startsWith("/admin") || pathname.startsWith("/login") || pathname.startsWith("/register") || engSm.enabled === false || !notifAllowed(system_messages, "nudge_engagement");

  useEffect(() => {
    if (hidden) return;
    if (sessionStorage.getItem(todayKey()) === "dismissed") return;
    // Avoid colliding with the bottom-left WelcomeNote: wait until it's dismissed.
    if (!localStorage.getItem("dh_welcome_seen")) return;
    let cancel = false;

    const pickNudge = (g) => {
      const poojaDone = localStorage.getItem("dharmartha_pooja_day") === new Date().toDateString();
      if (!user) return { msg: pick(engSm.hi || "निःशुल्क खाता बनाएँ — अपनी साधना संजोएँ, जप गिनें एवं अंक अर्जित करें।", engSm.en || "Create a free account — save your reading, track japa and earn sadhana points."), to: "/register", cta: pick(engSm.cta_hi || "जुड़ें", engSm.cta_en || "Join free"), icon: Sparkles };
      if (g?.level?.next_threshold && g.level.next_threshold - g.points <= 30) return { msg: pick(`केवल ${g.level.next_threshold - g.points} अंक शेष — ${g.level.next_en} स्तर तक!`, `Just ${g.level.next_threshold - g.points} points to reach ${g.level.next_en}!`), to: "/japa", cta: pick("जप करें", "Do japa"), icon: Flame };
      if (!poojaDone) return { msg: pick("आज की दैनिक पूजा अर्पित करें एवं अपनी निरंतरता बनाए रखें।", "Offer today's dainik pooja and keep your streak alive."), to: "/dainik-pooja", cta: pick("दैनिक पूजा", "Daily Pooja"), icon: Flame };
      const tips = [
        { msg: pick("किसी प्रश्न का उत्तर देकर प्रतिष्ठा अर्जित करें।", "Answer a question and earn reputation."), to: "/qna", cta: pick("प्रश्नोत्तर", "Q&A") },
        { msg: pick("नामस्मरण समूह में सम्मिलित होकर सामूहिक जप करें।", "Join a Namasmarana group and chant together."), to: "/namasmarana-groups", cta: pick("समूह", "Groups") },
        { msg: pick("आज की प्रश्नोत्तरी हल करें एवं अंक अर्जित करें।", "Take today's quiz and earn points."), to: "/quiz", cta: pick("प्रश्नोत्तरी", "Quiz") },
        { msg: pick("अपनी साधना की झलक देखें।", "See a glance of your sadhana."), to: "/sadhana", cta: pick("मेरी साधना", "My Sadhana") },
      ];
      return { ...tips[Math.floor(Math.random() * tips.length)], icon: Sparkles };
    };

    const run = (g) => { if (cancel) return; setNudge(pickNudge(g)); setTimeout(() => { if (!cancel && !isMenuOpen()) setShow(true); }, 5000); };
    if (user) fetchGamify().then(run).catch(() => run(null)); else run(null);
    return () => { cancel = true; };
  }, [user, hidden, pathname, pick]);

  // Rule 1: close (and never open over) the main menu.
  useEffect(() => onMenu((o) => { if (o) setShow(false); }), []);

  const dismiss = () => { setShow(false); sessionStorage.setItem(todayKey(), "dismissed"); };

  if (hidden || !show || !nudge) return null;
  const Icon = nudge.icon || Sparkles;
  return (
    <div data-testid="engagement-nudge" className="fixed bottom-5 left-5 z-[120] max-w-xs animate-in fade-in slide-in-from-bottom-4 border border-gold/50 bg-[var(--surface)] p-4 shadow-[0_16px_50px_rgba(199,80,0,0.14)] duration-500">
      <button onClick={dismiss} data-testid="nudge-dismiss" aria-label="Dismiss" className="absolute right-2 top-2 text-ink-3 hover:text-ink"><X className="h-4 w-4" /></button>
      <div className="flex items-start gap-3 pr-4">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--surface-alt)] text-saffron"><Icon className="h-4 w-4" /></span>
        <div>
          <p className="text-sm leading-relaxed text-ink-2">{nudge.msg}</p>
          <Link to={nudge.to} onClick={dismiss} data-testid="nudge-cta" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-saffron transition-transform hover:translate-x-0.5">
            {nudge.cta} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
